/** Recognise source links without navigating away from the current view. */
export function sourcePreviewReference(href: string | undefined, currentCaseId?: string) {
  if (!href) return null
  if (href.startsWith('evidence:') && currentCaseId)
    return { caseId: currentCaseId, referenceId: href.slice(9) }
  try {
    const origin = typeof window === 'undefined' ? 'https://spectre.local' : window.location.origin
    const url = new URL(href, origin)
    if (url.origin !== origin) return null
    const legacy = /^\/cases\/([^/]+)\/evidence\/([^/]+)$/.exec(url.pathname)
    if (legacy) return { caseId: legacy[1], referenceId: legacy[2] }
    const match = /^\/cases\/([^/]+)\/(?:agent|investigate)$/.exec(url.pathname)
    const referenceId = url.searchParams.get('sourcePreview')
    return match && referenceId ? { caseId: match[1], referenceId } : null
  } catch {
    return null
  }
}

/** The scheme an answer names a case document by: `[IMG_8848.jpeg](doc:<document_id>)`. */
const DOCUMENT_SCHEME = 'doc:'

/** A case document's id, with or without a moment after it (`#t=…`, a recording cited at a time,
 *  PRD-0074). The id is read strictly; anything else after `doc:` — a node id, a file name — is no
 *  document. The moment is read leniently: a model may write `4`, `4.5` or `00:04`. */
const DOCUMENT_ID =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:#t=([^#]*))?$/i

/** `125`, `4.5` or `2:05` → whole seconds; null for a moment that is none. */
function momentSeconds(fragment: string | undefined): number | null {
  if (fragment === undefined) return null
  const clock = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(fragment)
  if (clock) {
    const [, h, m, s] = clock
    return s === undefined
      ? Number(h) * 60 + Number(m)
      : Number(h) * 3600 + Number(m) * 60 + Number(s)
  }
  const seconds = Number(fragment)
  return Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : null
}

/** Whether a link is written as a document reference, whatever it names. */
export function isDocumentReference(href: string | undefined): boolean {
  return href?.startsWith(DOCUMENT_SCHEME) ?? false
}

/** A moment of a recording: the document and the second, as `doc:<id>#t=<seconds>` names it. */
export interface DocumentMoment {
  documentId: string
  /** Null for a document cited as a whole. */
  atSeconds: number | null
}

/** The document a `doc:` link names and, when it cites a moment, the second to open it at;
 *  null for every other link and for one that names no document. */
export function documentMoment(href: string | undefined): DocumentMoment | null {
  if (!href || !isDocumentReference(href)) return null
  const match = DOCUMENT_ID.exec(href.slice(DOCUMENT_SCHEME.length).trim())
  if (!match) return null
  return { documentId: match[1], atSeconds: momentSeconds(match[2]) }
}

/** Whether a link is one of the in-app references a markdown sanitizer must let through. */
export function isInAppReference(url: string): boolean {
  return isDocumentReference(url) || url.startsWith('evidence:')
}
